import { FC, useCallback, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "capitalrx-components-2.0/lib/forms";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold } from "capitalrx-components-2.0/lib/typography";

import { useSetMedicarePrescriptionPaymentPlanCommunicationPreferencesMutation } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ThemedButton, ThemedSubmitButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context";
import {
  useGetIsOptedIntoMedicarePrescriptionPaymentPlanEmailCommunications,
  useGetIsOptedIntoMedicarePrescriptionPaymentPlanSMSCommunications,
  useGetMedicarePrescriptionPaymentPlanEmail,
  useGetMedicarePrescriptionPaymentPlanFlag,
  useGetMedicarePrescriptionPaymentPlanPhoneNumber,
  useGetMedicarePrescriptionPaymentPlanWrittenCommunicationPreference,
  useIsMemberPrecludedFromParticipation,
} from "@/App/hooks";
import { usePlanYear } from "@/App/pages/MedicarePrescriptionPaymentPlan/PlanYearContext";

import { CommunicationPreferencesForm, CommunicationPreferencesFormData } from "./CommunicationPreferencesForm";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.communicationPreferencesPanel";

export const CommunicationPreferencesPanel: FC = () => {
  const [isEditing, setIsEditing] = useState(false);

  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const { isCompressed, PanelLayoutElement } = useResponsive({
    isCompressed: {
      mobile: true,
      tablet: false,
      desktop: false,
    },
    PanelLayoutElement: {
      mobile: Stack,
      desktop: Shelf,
    },
  });

  const alert = useAlert();

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);
  const { mutateAsync: setCommunicationPreferences } = useSetMedicarePrescriptionPaymentPlanCommunicationPreferencesMutation();
  const { isPlanYearNotOpen } = useIsMemberPrecludedFromParticipation(planYear);

  const canMakeChanges = !isPlanYearNotOpen;

  const displayAddress = useMemo(() => {
    const addressLine1 = memberEligibility?.member?.address_line_1 ?? "";
    const addressLine2 = memberEligibility?.member?.address_line_2 ?? "";
    const addressLine3 = memberEligibility?.member?.address_line_3 ?? "";

    const city = memberEligibility?.member?.city ?? "";
    const state = memberEligibility?.member?.state ?? "";
    const zip = memberEligibility?.member?.zip_code ?? "";

    let joinedAddressLines = "";
    if (addressLine1 || addressLine2 || addressLine3) {
      joinedAddressLines = [addressLine1, addressLine2, addressLine3]
        .filter((addressLine) => !!addressLine)
        .join(", ")
        .trim();
    }
    const cityStateZipLine = `${city}${city && state && ", "} ${state} ${zip}`.trim();

    const fullAddress = joinedAddressLines + `${joinedAddressLines ? ", " : ""}` + cityStateZipLine;
    return fullAddress.trim() || translate(`${TRANSLATION_PATH}.notApplicable`);
  }, [memberEligibility]);

  const { preference: writtenCommunicationPreference } = useGetMedicarePrescriptionPaymentPlanWrittenCommunicationPreference();
  const { isOptedIn: isOptedIntoSms } = useGetIsOptedIntoMedicarePrescriptionPaymentPlanSMSCommunications();
  const { isOptedIn: isOptedIntoEmail } = useGetIsOptedIntoMedicarePrescriptionPaymentPlanEmailCommunications();
  const { phoneNumber } = useGetMedicarePrescriptionPaymentPlanPhoneNumber();
  const { email } = useGetMedicarePrescriptionPaymentPlanEmail();

  const showMailCommunications = useMemo(() => {
    return ["mail", "both"].includes(writtenCommunicationPreference);
  }, [writtenCommunicationPreference, displayAddress]);

  const showEmailCommunications = useMemo(() => {
    return ["email", "both"].includes(writtenCommunicationPreference) && isOptedIntoEmail;
  }, [writtenCommunicationPreference, email, isOptedIntoEmail]);

  const showSmsCommunications = useMemo(() => {
    return isOptedIntoSms;
  }, [isOptedIntoSms, phoneNumber]);

  const isMemberAddressComplete = useMemo(() => {
    const addressLine1 = memberEligibility?.member?.address_line_1;
    const addressLine2 = memberEligibility?.member?.address_line_2;
    const addressLine3 = memberEligibility?.member?.address_line_3;

    const hasAtLeastOneAddressLine = !!addressLine1 || !!addressLine2 || !!addressLine3;

    const hasCity = !!memberEligibility?.member?.city;
    const hasState = !!memberEligibility?.member?.state;
    const hasZip = !!memberEligibility?.member?.zip_code;

    return hasAtLeastOneAddressLine && hasCity && hasState && hasZip;
  }, [memberEligibility]);

  const isMemberEmailComplete = useMemo(() => {
    return !!memberEligibility?.member?.email;
  }, [memberEligibility]);

  const defaultValues = useMemo((): CommunicationPreferencesFormData => {
    const addressLine2 = memberEligibility?.member?.address_line_2 || "";
    const addressLine3 = memberEligibility?.member?.address_line_3 || "";

    return {
      addressLine1: memberEligibility?.member?.address_line_1,
      addressLine2: addressLine2 + addressLine3,
      city: memberEligibility?.member?.city,
      state: memberEligibility?.member?.state,
      zipCode: memberEligibility?.member?.zip_code,
      phoneNumber: phoneNumber || memberEligibility?.member?.phone_number,
      emailAddress: email, // || memberEligibility?.member?.email, // fall back to member eligibility email
      optIntoEmail: showEmailCommunications,
      optIntoSms: showSmsCommunications,
      optIntoMail: showMailCommunications,
    };
  }, [memberEligibility, phoneNumber, email]);

  const onSaveCommunicationPreferences = useCallback(async (formData: CommunicationPreferencesFormData) => {
    try {
      await setCommunicationPreferences({
        opt_into_email: formData.optIntoEmail,
        opt_into_mail: formData.optIntoMail,
        opt_into_sms: formData.optIntoSms,
        phone_number: formData.optIntoSms ? formData.phoneNumber?.replaceAll?.(/[ .-]+/g, "")?.trim?.() : undefined,
        email: formData.optIntoEmail ? formData.emailAddress?.trim?.() : undefined,
        plan_year: planYear,
      });
      alert({
        type: "success",
        message: translate(`${TRANSLATION_PATH}.communicationPreferencesUpdated`),
        style: "toast",
      });
      setIsEditing(false);
    } catch (e) {
      alert({
        type: "error",
        message: translate(`${TRANSLATION_PATH}.communicationPreferencesUpdateError`),
        style: "toast",
      });
    }
  }, [setCommunicationPreferences, planYear, alert, translate]);

  const form = useForm<CommunicationPreferencesFormData>({
    defaultValues: { ...defaultValues },
  });

  const onCancelEdit = useCallback(() => {
    setIsEditing(false);
    form.reset(defaultValues);
  }, [defaultValues]);

  if (!flag) {
    return <></>;
  }

  return (
    <Stack>
      <Heading>{translate(`${TRANSLATION_PATH}.communicationPreferences`)}</Heading>
      <Grid>
        {showMailCommunications && (
          <Grid.Span columns={12}>
            <PanelLayoutElement spacing={isCompressed ? "small" : "large"}>
              <Stack testId="mail" spacing="none">
                <Semibold>{translate(`${TRANSLATION_PATH}.communicationPreference`)}</Semibold>
                <Body>{translate(`${TRANSLATION_PATH}.mail`)}</Body>
              </Stack>
              <Stack spacing="none">
                <Semibold>{translate(`${TRANSLATION_PATH}.mailingAddress`)}</Semibold>
                <Body>{displayAddress}</Body>
              </Stack>
              <Shelf.Spacer />
            </PanelLayoutElement>
          </Grid.Span>
        )}
        {showEmailCommunications && (
          <Grid.Span columns={12}>
            <PanelLayoutElement spacing={isCompressed ? "small" : "large"}>
              <Stack testId="email" spacing="none">
                <Semibold>{translate(`${TRANSLATION_PATH}.communicationPreference`)}</Semibold>
                <Body>{translate(`${TRANSLATION_PATH}.email`)}</Body>
              </Stack>
              <Stack spacing="none">
                <Semibold>{translate(`${TRANSLATION_PATH}.emailAddress`)}</Semibold>
                <Body>{email}</Body>
              </Stack>
              <Shelf.Spacer />
            </PanelLayoutElement>
          </Grid.Span>
        )}
        {showSmsCommunications && (
          <Grid.Span columns={12}>
            <PanelLayoutElement spacing={isCompressed ? "small" : "large"}>
              <Stack testId="sms" spacing="none">
                <Semibold>{translate(`${TRANSLATION_PATH}.communicationPreference`)}</Semibold>
                <Body>{translate(`${TRANSLATION_PATH}.textSMS`)}</Body>
              </Stack>
              <Stack spacing="none">
                <Semibold>{translate(`${TRANSLATION_PATH}.cellPhoneNumber`)}</Semibold>
                <Body>{phoneNumber}</Body>
              </Stack>
              <Shelf.Spacer />
            </PanelLayoutElement>
          </Grid.Span>
        )}
        {!showEmailCommunications && !showSmsCommunications && !showMailCommunications && (
          <Body>{translate(`${TRANSLATION_PATH}.youAreNotReceivingCommunications`)}</Body>
        )}
      </Grid>
      {canMakeChanges &&!isEditing && (
        <Shelf>
          <ThemedButton testId="changedPreferences" appearance="text" onClick={() => setIsEditing(true)}>
            {translate(`${TRANSLATION_PATH}.changePreferences`)}
          </ThemedButton>
          <Shelf.Spacer />
        </Shelf>
      )}
      {canMakeChanges && isEditing && (
        <Form form={form} onSubmit={onSaveCommunicationPreferences}>
          <Stack>
            <CommunicationPreferencesForm addressComplete={isMemberAddressComplete} emailComplete={isMemberEmailComplete} />
            {isCompressed && (
              <Grid>
                <ThemedSubmitButton testId="save-communication-preference">{translate(`${TRANSLATION_PATH}.save`)}</ThemedSubmitButton>
                <Grid.Break />
                <ThemedButton testId="cancel-communication-preference" appearance="secondary" onClick={onCancelEdit}>
                  {translate(`${TRANSLATION_PATH}.cancel`)}
                </ThemedButton>
              </Grid>
            )}
            {!isCompressed && (
              <Shelf>
                <ThemedSubmitButton testId="save-communication-preference">{translate(`${TRANSLATION_PATH}.save`)}</ThemedSubmitButton>
                <ThemedButton testId="cancel-communication-preference" appearance="secondary" onClick={onCancelEdit}>
                  {translate(`${TRANSLATION_PATH}.cancel`)}
                </ThemedButton>
                <Shelf.Spacer />
              </Shelf>
            )}
          </Stack>
        </Form>
      )}
    </Stack>
  );
};
